import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, Upload, Loader2, CheckCircle2, XCircle, Database } from "lucide-react";
import { toast } from "sonner";

const COLS = [
  ["myob_mapping", "MYOB Map"], ["sales_budget", "Budget"], ["timesheets", "Timesheets"],
  ["lost_time_injuries", "LTI"], ["compliments", "Compliments"],
  ["complaints_received", "Compl. Rec"], ["complaints_outstanding", "Compl. Out"],
  ["action_item_score", "Action Items"],
];

export function DataCompletenessPanel({ month, canEdit }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get(`/qms/data-completeness?month=${month}`).then(({ data: d }) => setData(d)).catch(() => {});
  };
  useEffect(() => { if (open && month) load(); /* eslint-disable-next-line */ }, [open, month]);

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data: r } = await api.post(`/qms/customer-kpis/import?month=${month}`, form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      toast.success(`${r.kpi_values_upserted} KPI values imported`);
      if (r.unknown_bus?.length) toast.warning(`Unknown BU codes skipped: ${r.unknown_bus.join(", ")}`, { duration: 8000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const incomplete = data ? data.rows.filter((r) => !r.complete).length : null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="perf-completeness-panel">
      <button data-testid="perf-completeness-toggle" className="w-full flex items-center justify-between px-4 py-3"
        onClick={() => setOpen((v) => !v)}>
        <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
          <Database className="w-4 h-4 text-slate-400" /> Data completeness — {month}
          {incomplete != null && (
            <Badge className={`${incomplete ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"} hover:bg-inherit text-[10px]`}>
              {incomplete ? `${incomplete} BUs with gaps` : "all complete"}
            </Badge>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && data && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(data.org_checks).map(([k, ok]) => (
              <Badge key={k} className={`${ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"} hover:bg-inherit text-[10px]`} data-testid={`perf-org-check-${k}`}>
                {ok ? "✓" : "✗"} {k.replace(/_/g, " ")}
              </Badge>
            ))}
            <span className="text-[11px] text-slate-500">{data.gp_budget_note}</span>
            {canEdit && (
              <>
                <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" data-testid="perf-kpi-file-input"
                  onChange={(e) => upload(e.target.files?.[0])} />
                <Button data-testid="perf-kpi-upload-btn" size="sm" variant="outline" className="h-7 text-xs ml-auto" disabled={busy}
                  onClick={() => fileRef.current?.click()}>
                  {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                  Import customer KPIs
                </Button>
              </>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="perf-completeness-table">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left px-2 py-1.5">BU</th>
                  {COLS.map(([k, label]) => <th key={k} className="text-center px-2 py-1.5" title={data.legend[k]}>{label}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.bu_id} className={`border-t border-slate-100 ${r.complete ? "" : "bg-amber-50/30"}`}>
                    <td className="px-2 py-1.5 font-medium text-slate-800 whitespace-nowrap">{r.bu_code} <span className="text-slate-400 font-normal hidden md:inline">{r.bu_name}</span></td>
                    {COLS.map(([k]) => (
                      <td key={k} className="text-center px-2 py-1.5">
                        {r.checks[k] ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 inline" /> : <XCircle className="w-3.5 h-3.5 text-rose-400 inline" />}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-slate-400">
            Spreadsheet columns: BU, [Month], Compliments, Complaints Received, Complaints Outstanding, [LTI], [Action Item Score]. JSA/CCEW/MYOB are org-level feeds shown as chips above.
          </div>
        </div>
      )}
    </div>
  );
}
